import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, DollarSign, Calendar, Ticket, Download, RefreshCw, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AnalyticsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/dashboard/analytics?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCustomers = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/customers/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clientes_gsagency_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleExportOrders = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/orders/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ordenes_gsagency_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const summary = data?.summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <BarChart3 className="w-6 h-6 mr-2 text-amber-500" />
            Analytics
          </h2>
          <p className="text-gray-400 text-sm">Métricas y reportes de eventos</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700"
          >
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="90d">Últimos 90 días</option>
            <option value="all">Todo el tiempo</option>
          </select>
          <button
            onClick={fetchAnalytics}
            className="p-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-5 border border-green-500/20">
          <div className="flex items-center justify-between mb-3">
            <DollarSign className="w-8 h-8 text-green-500" />
            <span className="text-green-400 text-xs font-medium bg-green-500/20 px-2 py-1 rounded-full">
              Ingresos
            </span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(summary.total_revenue || 0)}</p>
          <p className="text-green-400/70 text-sm mt-1">Total ventas</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-5 border border-blue-500/20">
          <div className="flex items-center justify-between mb-3">
            <Ticket className="w-8 h-8 text-blue-500" />
            <span className="text-blue-400 text-xs font-medium bg-blue-500/20 px-2 py-1 rounded-full">
              Tickets
            </span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_tickets || 0}</p>
          <p className="text-blue-400/70 text-sm mt-1">{summary.total_orders || 0} órdenes</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-5 border border-purple-500/20">
          <div className="flex items-center justify-between mb-3">
            <Users className="w-8 h-8 text-purple-500" />
            <span className="text-purple-400 text-xs font-medium bg-purple-500/20 px-2 py-1 rounded-full">
              Clientes
            </span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_customers || 0}</p>
          <p className="text-purple-400/70 text-sm mt-1">
            {summary.new_customers || 0} nuevos, {summary.returning_customers || 0} recurrentes
          </p>
        </div>

        <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl p-5 border border-amber-500/20">
          <div className="flex items-center justify-between mb-3">
            <TrendingUp className="w-8 h-8 text-amber-500" />
            <span className="text-amber-400 text-xs font-medium bg-amber-500/20 px-2 py-1 rounded-full">
              Promedio
            </span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(summary.avg_order_value || 0)}</p>
          <p className="text-amber-400/70 text-sm mt-1">Valor por orden</p>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <p className="text-gray-400 text-sm mb-1">Eventos Activos</p>
          <p className="text-xl font-bold text-white">{summary.total_events || 0}</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <p className="text-gray-400 text-sm mb-1">Tasa de Check-in</p>
          <p className="text-xl font-bold text-white">{summary.checkin_rate || 0}%</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <p className="text-gray-400 text-sm mb-1">Invitaciones Confirmadas</p>
          <p className="text-xl font-bold text-white">
            {data?.invitations?.confirmed || 0} / {data?.invitations?.total || 0}
          </p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Events */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-amber-500" />
            Top Eventos por Ventas
          </h3>
          {data?.top_events?.length > 0 ? (
            <div className="space-y-3">
              {data.top_events.map((event, index) => (
                <div key={index} className="bg-zinc-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-medium">{event.event}</p>
                    <p className="text-amber-400 font-bold">{formatCurrency(event.revenue)}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span>{event.tickets} tickets</span>
                    <span>{event.orders} órdenes</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Sin datos de eventos</p>
          )}
        </div>

        {/* Upcoming Events */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-amber-500" />
            Próximos Eventos
          </h3>
          {data?.upcoming_events?.length > 0 ? (
            <div className="space-y-3">
              {data.upcoming_events.map((event, index) => (
                <div key={index} className="bg-zinc-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-medium">{event.name?.es || 'Evento'}</p>
                    <p className="text-gray-400 text-sm">{event.date}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 h-2 bg-zinc-700 rounded-full overflow-hidden mr-4">
                      <div 
                        className="h-full bg-amber-500"
                        style={{ width: `${event.total_capacity > 0 ? (event.total_sold / event.total_capacity) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-gray-400 text-sm whitespace-nowrap">
                      {event.total_sold || 0}/{event.total_capacity || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Sin eventos próximos</p>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Órdenes Recientes</h3>
        {data?.recent_orders?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-3 text-gray-400 font-medium text-sm">Cliente</th>
                  <th className="text-left py-3 text-gray-400 font-medium text-sm">Evento</th>
                  <th className="text-left py-3 text-gray-400 font-medium text-sm">Total</th>
                  <th className="text-left py-3 text-gray-400 font-medium text-sm">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_orders.map((order) => (
                  <tr key={order.order_id} className="border-b border-zinc-800/50">
                    <td className="py-3 text-white">{order.customer_name}</td>
                    <td className="py-3 text-gray-400">{order.event_name}</td>
                    <td className="py-3 text-amber-400 font-medium">{formatCurrency(order.total_amount)}</td>
                    <td className="py-3 text-gray-500 text-sm">
                      {new Date(order.created_at).toLocaleDateString('es-MX')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">Sin órdenes recientes</p>
        )}
      </div>

      {/* Export Buttons */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Download className="w-5 h-5 mr-2 text-amber-500" />
          Exportar Datos
        </h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleExportCustomers}
            className="flex items-center px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar Clientes (CSV)
          </button>
          <button
            onClick={handleExportOrders}
            className="flex items-center px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar Órdenes (CSV)
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
